import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * True when a dialog other than `current` is still open.
 * Stacked modals (cart drawer + engraving preview) must not release the page scroll lock
 * until the last dialog closes.
 *
 * @param {HTMLDialogElement | undefined} current
 * @returns {boolean}
 */
function anotherDialogIsOpen(current) {
  for (const dialog of document.querySelectorAll('dialog')) {
    if (dialog !== current && dialog.open) return true;
  }

  return false;
}

/**
 * Clears body scroll-lock styles applied by DialogComponent.showDialog().
 * Leaves the lock in place while another dialog is still open.
 *
 * @param {HTMLDialogElement | undefined} [current]
 */
function unlockBodyScroll(current) {
  if (anotherDialogIsOpen(current)) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('close', this.#handleDialogClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.dialog?.removeEventListener('close', this.#handleDialogClose);
    unlockBodyScroll(this.refs.dialog);

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const bodyIsLocked = document.body.style.position === 'fixed';
    const scrollY = window.scrollY;

    if (!bodyIsLocked) {
      this.#previousScrollY = scrollY;
    } else {
      const lockedTop = Number.parseInt(document.body.style.top, 10);

      if (!Number.isNaN(lockedTop)) {
        this.#previousScrollY = Math.abs(lockedTop);
      }
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // Component may have disconnected before this frame; avoid re-locking body
      if (!this.isConnected || dialog.open) return;

      if (document.body.style.position !== 'fixed') {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) {
      unlockBodyScroll(dialog);
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (!anotherDialogIsOpen(dialog)) {
      unlockBodyScroll(dialog);
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Ensures body scroll-lock styles are cleared when the dialog closes by any path.
   */
  #handleDialogClose = () => {
    const wasBodyFixed = document.body.style.position === 'fixed';
    const preserveLock = anotherDialogIsOpen(this.refs.dialog);

    if (!preserveLock) {
      unlockBodyScroll(this.refs.dialog);

      if (wasBodyFixed) {
        window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      }
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

/**
 * Clear leftover scroll locks when restoring from bfcache (e.g. Account → back to Homepage).
 * @param {PageTransitionEvent} event
 */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  unlockBodyScroll();
  document.documentElement.removeAttribute('scroll-lock');
});
